/** EN catalog — family connections (ZOY-665): the patient tab, the workspace, the inbox. */
export const familyEn: Record<string, string> = {
  'nav.family': 'Family',
  'rec.tab.family': 'Family',

  // statuses (Auth's words)
  'family.st.pending': 'Pending',
  'family.st.accepted': 'Connected',
  'family.st.declined': 'Declined',
  'family.st.expired': 'Expired',
  'family.st.cancelled': 'Cancelled',
  'family.st.withdrawn': 'Withdrawn by the family',
  'family.st.superseded': 'Superseded',
  'family.st.ended': 'Ended',
  'family.local.creating': 'Sending…',
  'family.local.failed': 'Not sent',
  'family.kind.invitation': 'Invitation',
  'family.kind.request': 'Family request',
  'family.kind.connection': 'Connection',
  'family.endedBy.clinic_ended': 'ended by the clinic',
  'family.endedBy.family_ended': 'ended by the family',

  // the patient tab
  'family.tab.title': 'Family connection',
  'family.tab.sub':
    'The family account this record is connected to, and every invitation on the way there. The family sees the care plan through this connection.',
  'family.tab.guest':
    'A Guest record cannot be connected to a family. Complete the registration first.',
  'family.tab.merged': 'This record was merged into another one; its connection lives there.',
  'family.tab.current': 'Connected family',
  'family.tab.currentSince': 'Connected since {date}',
  'family.tab.accountName': 'Family account',
  'family.tab.accountUnknown': 'Name not shared yet',
  'family.tab.nameNote':
    'The name is the family account’s own display name — shown for context only. A difference from the caregiver on file changes nothing by itself.',
  'family.tab.noConnection': 'No family connected',
  'family.tab.noConnectionBody':
    'Invite one of the numbers on this record. The invitation appears in the Family app once that number signs in; nothing is sent by SMS.',
  'family.tab.open': 'Waiting for an answer',
  'family.tab.history': 'History',
  'family.tab.historyEmpty': 'Nothing yet.',
  'family.tab.inconsistent':
    'Two connections still read as active — the clinic system is catching up with a recent change. Refresh in a moment.',
  'family.tab.synced': 'Last synced {when}',
  'family.tab.neverSynced': 'Not synced yet',
  'family.tab.refresh': 'Refresh',
  'family.tab.refreshRequested': 'Refresh requested — the list updates on the next sync.',
  'family.tab.invite': 'Invite a family member',
  'family.tab.needsManage':
    'Inviting and ending connections needs the family-management permission.',

  // invite dialog
  'family.invite.title': 'Invite a family member',
  'family.invite.body':
    'Choose one of the numbers already on this record. The person holding that number sees the invitation in the Family app after signing in with it.',
  'family.invite.mobile': 'Number to invite',
  'family.invite.contactLabel': 'Record contact number',
  'family.invite.note': 'Note for the family (optional)',
  'family.invite.noteHelp':
    'Shown only inside the Family app, never in a message. Keep patient details minimal.',
  'family.invite.send': 'Send invitation',
  'family.invite.noNumbers': 'This record has no number to invite.',
  'family.invite.sent': 'Invitation sent',
  'family.invite.sentBody': 'It stays open for 7 days. You can resend it or cancel it below.',

  // row facts and actions
  'family.row.to': 'To {mobile}',
  'family.row.from': 'From {name} · {mobile}',
  'family.row.fromUnknown': 'From an unnamed family account',
  'family.row.message': 'Their message: {text}',
  'family.row.note': 'Note: {text}',
  'family.row.sent': 'Sent {n, plural, one {once} other {# times}} · last {when}',
  'family.row.expires': 'Expires {when}',
  'family.row.decided': '{when}',
  'family.row.declineReason': 'Reason: {reason}',
  'family.row.declineNote': '“{note}”',
  'family.row.ended': 'Ended {when} — {by}',
  'family.row.accepted': 'Accepted {when}',
  'family.row.by': 'by {name}',
  'family.row.failed':
    'The clinic system could not complete this. Invite the same number again to retry.',
  'family.act.resend': 'Resend',
  'family.act.cancel': 'Cancel invitation',
  'family.act.end': 'End connection',
  'family.act.accept': 'Accept…',
  'family.act.decline': 'Decline…',
  'family.act.resendWait': 'Resend available in {wait}',
  'family.act.resent': 'Invitation resent',
  'family.act.cancelled': 'Invitation cancelled',

  // end connection
  'family.end.title': 'End this family connection?',
  'family.end.body':
    'The family stops receiving anything new from this record’s care plan. What was already shared stays visible to them as history — nothing is taken back.',
  'family.end.effectTitle': 'What changes',
  'family.end.effect':
    'The clinic keeps its own history. A new invitation can connect the family again later.',
  'family.end.confirm': 'End connection',
  'family.end.done': 'Connection ended',

  // decline reasons
  'family.reason.wrong_number': 'Wrong number',
  'family.reason.not_now': 'Not now',
  'family.reason.unknown_clinic': 'Does not know this clinic',
  'family.reason.other': 'Other',
  'family.decline.title': 'Decline this request',
  'family.decline.body': 'The family sees the reason you choose, and your note if you add one.',
  'family.decline.reason': 'Reason',
  'family.decline.note': 'Note for the family (optional)',
  'family.decline.confirm': 'Decline request',
  'family.decline.done': 'Request declined',

  // accept — explicit patient selection
  'family.accept.title': 'Accept the request for a patient',
  'family.accept.body':
    'A request names no patient. Choose the registered patient this family is asking about — the connection is created for that record only.',
  'family.accept.requester': 'Requested by {name} · {mobile}',
  'family.accept.byMobile': 'Records holding this number',
  'family.accept.byMobileNone': 'No record holds this number.',
  'family.accept.search': 'Search a patient',
  'family.accept.searchHelp': 'Name or file number — registered patients only.',
  'family.accept.searchNone': 'No registered patient matches.',
  'family.accept.pick': 'Choose',
  'family.accept.picked': 'Chosen',
  'family.accept.guestSkipped': 'Guest — cannot be connected',
  'family.accept.confirm': 'Accept for this patient',
  'family.accept.needsChoice': 'Choose a patient first.',
  'family.accept.done': 'Request accepted — the family is connected.',
  'family.accept.file': 'File {n}',

  // workspace
  'family.ws.title': 'Family connections',
  'family.ws.sub':
    'Every invitation, family request and connection of the clinic. What you see here is the clinic system’s copy; the family system decides.',
  'family.ws.tabLinks': 'All connections',
  'family.ws.tabRequests': 'Requests',
  'family.ws.colPatient': 'Patient',
  'family.ws.colKind': 'Kind',
  'family.ws.colStatus': 'Status',
  'family.ws.colWith': 'Family',
  'family.ws.colWhen': 'Updated',
  'family.ws.colBy': 'By',
  'family.ws.fKind': 'Kind',
  'family.ws.fStatus': 'Status',
  'family.ws.fAll': 'All',
  'family.ws.fMine': 'Mine only',
  'family.ws.fFrom': 'From',
  'family.ws.fTo': 'To',
  'family.ws.fPatient': 'One patient',
  'family.ws.clearPatient': 'Show every patient',
  'family.ws.patientHidden': 'Patient names need the patients permission.',
  'family.ws.emptyTitle': 'No family connections yet',
  'family.ws.emptyBody':
    'Invitations start from a patient’s Family tab. Family requests to the clinic appear under Requests.',
  'family.ws.noResultsTitle': 'Nothing matches these filters',
  'family.ws.noResultsBody': 'Loosen a filter or clear them.',
  'family.ws.openPatient': 'Open',
  'family.ws.requestsEmptyTitle': 'No pending requests',
  'family.ws.requestsEmptyBody':
    'When a family asks to connect to this clinic, the request appears here.',
  'family.ws.requestsSub':
    'Families who asked to connect to this clinic. Each shows the name and verified number the family chose to share. Accepting always means choosing a patient yourself.',
  'family.ws.pending': '{n, plural, one {# pending request} other {# pending requests}}',

  // errors
  'family.err.generic': 'The clinic system refused this action.',
  'family.err.alreadyConnected':
    'This patient already has a connected family. End it before inviting another number.',
  'family.err.notRegistered': 'Only a registered patient can be connected.',
  'family.err.mobileNotOnRecord':
    'That number is not on this record. Add it as a caregiver first, then invite it.',
  'family.err.mobileNotCanonical':
    'This number is not stored in the international form. Correct it on the record first.',
  'family.err.quota': 'The family system asks for a wait before sending again.',
  'family.err.invalidState': 'This item has moved on — refresh to see its current state.',
  'family.err.notFound': 'This item no longer exists.',
  'family.err.authRefused': 'The family system did not accept this action from you.',
  'family.err.clinicInactive': 'This clinic is currently inactive; family connections are frozen.',
  'family.err.unavailable':
    'The family system cannot be reached right now. Nothing was changed — try again later.',
  'family.err.wait': 'Try again in {wait}.',
};
